import { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { CERTIFICATES } from '../../../data/certificates';
import '../../../styles/ecerts.scss';

export function ECertificates() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [sectionVisible, setSectionVisible] = useState(false);
  const sectionRef = useRef(null);

  // Scroll-driven active index
  useEffect(() => {
    const handleScroll = () => {
      if (!sectionRef.current) return;
      const rect = sectionRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const isVisible = rect.bottom >= 0 && rect.top <= windowHeight;

      setSectionVisible(isVisible);

      if (isVisible) {
        const sectionProgress = Math.max(0, Math.min(1,
          (windowHeight - rect.top) / (windowHeight + rect.height)
        ));
        const newIndex = Math.min(
          Math.floor(sectionProgress * CERTIFICATES.length),
          CERTIFICATES.length - 1
        );
        setCurrentIndex(newIndex);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const progressPct = ((currentIndex + 1) / CERTIFICATES.length) * 100;

  return (
    <section className="e-certs" id="ecerts" ref={sectionRef} aria-label="E-Certificates">

      {/* Section header */}
      <div className="e-certs-header">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.55 }}
        >
          <span className="e-certs-eyebrow">Continuous learning</span>
          <h2 className="e-certs-title-text">E-Certificates</h2>
          <span className="accent-line" aria-hidden="true" />
          <p className="e-certs-subtitle">
            Online certifications completed through Coursera — scroll through to explore.
          </p>
        </motion.div>
      </div>

      {/* Sticky floating title */}
      <div className="e-certificates" aria-hidden="true">
        <div className="certificates-container">
          {CERTIFICATES.map((cert, index) => (
            <figure
              key={cert.id}
              className={index === currentIndex ? 'active' : ''}
              aria-hidden={index !== currentIndex}
            >
              <img
                src={cert.image}
                alt={`${cert.title} certificate`}
                loading={index === 0 ? 'eager' : 'lazy'}
              />
            </figure>
          ))}
        </div>
      </div>

      {/* Progress counter badge */}
      <div
        className={`e-cert-counter${sectionVisible ? ' visible' : ''}`}
        aria-live="polite"
        aria-label={`Certificate ${currentIndex + 1} of ${CERTIFICATES.length}`}
      >
        <span>{currentIndex + 1} / {CERTIFICATES.length}</span>
        <div className="e-cert-counter-bar">
          <div className="e-cert-counter-fill" style={{ width: `${progressPct}%` }} />
        </div>
      </div>

    </section>
  );
}
